import React, {useMemo} from "react"

import {findIndex} from "lodash"
import PropTypes from "prop-types"

import {useIsResponsive} from "../../hooks/useIsResponsive"
import {isKeyboardClickEvent} from "../../utils/accessibilityUtil"
import StepContent from "./components/StepContent"
import texts from "./texts"

import {useRouter} from "../../hooks/useRouter"
import "./style.scss"

const Stepper = ({pagesArchitecture, steps}) => {
    const {navigate, location} = useRouter()
    const isResponsive = useIsResponsive()

    const stepsToDisplay = useMemo(() => {
        const indexCurrentStep = findIndex(steps, step => location.pathname === pagesArchitecture[step.page]?.route)

        return steps
            .map((step, index) => {
                // Les étapes ne sont pas cliquables à partir de celle correspondant à la page actuelle
                const isCurrentStep = index === indexCurrentStep
                const isClickable = (step.clickable || step.clickable === undefined) && index < indexCurrentStep
                const isDisabled = index > indexCurrentStep
                const statusSuffix = isCurrentStep
                    ? texts.etapeActuelle
                    : index < indexCurrentStep
                    ? texts.etapeCompletee
                    : index > indexCurrentStep
                    ? texts.etapeAVenir
                    : ""

                return {
                    ...step,
                    isCurrentStep,
                    isClickable,
                    isDisabled,
                    statusSuffix,
                }
            })
            .filter(step => !step.hide)
    }, [location, pagesArchitecture, steps])

    const handleClick = step => {
        if (step.isClickable) {
            navigate(pagesArchitecture[step.page].route)
        }
    }

    const handleKeyDown = (e, step) => {
        if (isKeyboardClickEvent(e) && step.isClickable) {
            e.preventDefault()
            handleClick(step)
        }
    }

    return (
        <nav aria-label={texts.navigationEtapes} className="stepper">
            {stepsToDisplay?.map((step, index) => {
                const stepLabel = `${texts.etape} ${index + 1} : ${step.name}${step.statusSuffix}`
                const isLastStep = index + 1 === stepsToDisplay.length

                return step.isClickable ? (
                    <button
                        aria-current={step.isCurrentStep ? "step" : undefined}
                        aria-label={stepLabel}
                        className="stepper-item clickable"
                        key={`key-${step.name}`}
                        onClick={() => handleClick(step)}
                        onKeyDown={e => handleKeyDown(e, step)}
                        type="button"
                    >
                        <StepContent
                            index={index}
                            isLastStep={isLastStep}
                            isResponsive={isResponsive}
                            name={step.name}
                        />
                    </button>
                ) : (
                    <div
                        aria-current={step.isCurrentStep ? "step" : undefined}
                        aria-label={stepLabel}
                        className={`stepper-item${step.isDisabled ? " disabled" : ""}${
                            step.isCurrentStep ? " current-step" : ""
                        }`}
                        key={`key-${step.name}`}
                        role="listitem"
                        tabIndex={-1}
                    >
                        <StepContent
                            index={index}
                            isLastStep={isLastStep}
                            isResponsive={isResponsive}
                            name={step.name}
                        />
                    </div>
                )
            })}
        </nav>
    )
}

Stepper.propTypes = {
    pagesArchitecture: PropTypes.object.isRequired,
    steps: PropTypes.array.isRequired,
}

export default Stepper
